import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";

import { IconButton } from "@/components/ds";

// Transient notifications. From d2d-UEM ds-uem/, not in d2d-XDR ds/ (d2d-UEM plan 2.9): SIEM's ds/ has no
// toast, and UEM had two, @datasirpi/ui's (66 call sites) and a local one in
// components/Toast.tsx whose only live job was surfacing `api-error` window
// events. This is one provider for both.
//
// The API is @datasirpi/ui's, name for name, so Phase 3 swaps the import and
// nothing else: toast({ title, description, variant, duration, action }).
//
// Status is never colour alone: every toast carries its icon, and danger is
// announced assertively. Timers pause while a toast is hovered or focused, so
// a message never disappears under the pointer of someone reading it.

export type ToastVariant = "info" | "success" | "warning" | "danger";
export type ToastPosition = "top-end" | "top-start" | "bottom-end" | "bottom-start";

export interface ToastOptions {
  title?: ReactNode;
  /** Body text below the title. Either `title` or `description` must be set. */
  description?: ReactNode;
  /** Default "info". */
  variant?: ToastVariant;
  /** Auto-dismiss after this many ms. 0 or Infinity keeps it open. Default 5000. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastItem extends ToastOptions {
  id: string;
}

export interface ToastContextValue {
  toasts: ToastItem[];
  toast: (opts: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastProviderProps {
  /** Where the stack sits. Default "top-end". */
  position?: ToastPosition;
  /** Most toasts visible at once; the oldest drop first. Default 5. */
  max?: number;
  /** Where to portal. Default document.body. */
  container?: HTMLElement | null;
  children: ReactNode;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastVariant, typeof Info> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
};

/** The `api-error` event lib/api.ts dispatches (e.g. permission denied). */
type ApiErrorDetail = { type?: "success" | "error" | "info" | "warning"; message: string };
const API_ERROR_VARIANT: Record<NonNullable<ApiErrorDetail["type"]>, ToastVariant> = {
  success: "success",
  error: "danger",
  info: "info",
  warning: "warning",
};

let seq = 0;

export function ToastProvider({ position = "top-end", max = 5, container, children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const dismiss = useCallback((id: string) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const dismissAll = useCallback(() => setToasts([]), []);
  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = `toast-${++seq}`;
      setToasts((all) => [...all, { ...opts, id }].slice(-max));
      return id;
    },
    [max]
  );

  useEffect(() => {
    const onApiError = (e: Event) => {
      const detail = (e as CustomEvent<ApiErrorDetail>).detail;
      if (!detail?.message) return;
      toast({ description: detail.message, variant: API_ERROR_VARIANT[detail.type ?? "error"] });
    };
    window.addEventListener("api-error", onApiError);
    return () => window.removeEventListener("api-error", onApiError);
  }, [toast]);

  const value = useMemo(() => ({ toasts, toast, dismiss, dismissAll }), [toasts, toast, dismiss, dismissAll]);
  const target = container ?? (mounted ? document.body : null);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {target
        ? createPortal(
            <div className="rc-toast-region" data-position={position} aria-label="Notifications">
              {toasts.map((t) => (
                <Toast key={t.id} item={t} onDismiss={dismiss} />
              ))}
            </div>,
            target
          )
        : null}
    </ToastContext.Provider>
  );
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void }) {
  const { id, title, description, variant = "info", duration = 5000, action } = item;
  const Icon = ICONS[variant];
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!duration || !Number.isFinite(duration) || paused) return;
    startedAt.current = Date.now();
    const t = window.setTimeout(() => onDismiss(id), remaining.current);
    return () => {
      window.clearTimeout(t);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [duration, paused, id, onDismiss]);

  return (
    <div
      className="rc-toast"
      data-variant={variant}
      role={variant === "danger" ? "alert" : "status"}
      aria-live={variant === "danger" ? "assertive" : "polite"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
    >
      <Icon className="rc-toast__icon" aria-hidden="true" />
      <div className="rc-toast__body">
        {title ? <p className="rc-toast__title">{title}</p> : null}
        {description ? <p className="rc-toast__description">{description}</p> : null}
        {action ? (
          <button
            type="button"
            className="rc-toast__action"
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
          >
            {action.label}
          </button>
        ) : null}
      </div>
      <IconButton
        aria-label="Dismiss notification"
        tooltip={false}
        size="sm"
        icon={<X />}
        className="rc-toast__close"
        onClick={() => onDismiss(id)}
      />
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
